import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import FileTree from '../components/FileTree';
import EditorTabs from '../components/EditorTabs';
import Terminal from '../components/Terminal';
import { ideApi } from '../services/ideApi';
import './IDE.css';

function IDE() {
    const navigate = useNavigate();
    const location = useLocation();

    const editorRef = useRef(null);

    const [files, setFiles] = useState(null);
    const [fileContents, setFileContents] = useState({});
    const [openFiles, setOpenFiles] = useState([]);
    const [activeFile, setActiveFile] = useState(null);
    const [terminalHeight, setTerminalHeight] = useState(200);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Keep refs in sync so Monaco keybindings always see the latest state
    const activeFileRef = useRef(activeFile);
    const fileContentsRef = useRef(fileContents);

    useEffect(() => {
        activeFileRef.current = activeFile;
    }, [activeFile]);

    useEffect(() => {
        fileContentsRef.current = fileContents;
    }, [fileContents]);

    const loadFileTree = async () => {
        try {
            setLoading(true);
            const tree = await ideApi.getFileTree();
            setFiles(tree);
            setError(null);
        } catch (err) {
            console.error('Failed to load file tree:', err);
            setError('IDE backend is unavailable. The demo should still load sample files; try refresh.');
        } finally {
            setLoading(false);
        }
    };

    // Load file tree on mount
    useEffect(() => {
        loadFileTree();
    }, []);

    // If coming from Notebook, open the passed content as a file in the IDE
    useEffect(() => {
        if (loading) return;
        if (!files) return;

        const state = location?.state;
        if (!state?.fromNotebook) return;

        const suggestedName = state.fileName || 'notebook.py';
        const suggestedContent = state.content || '';

        const path = suggestedName.includes('/') ? suggestedName : `workspace/${suggestedName}`;

        setFileContents((prev) => ({
            ...prev,
            [path]: suggestedContent,
        }));

        setOpenFiles((prev) => (prev.includes(path) ? prev : [...prev, path]));
        setActiveFile(path);
    }, [loading, files, location]);

    // Get file content from cache or fetch from backend
    const getFileContent = async (path) => {
        if (fileContents[path] !== undefined) {
            return fileContents[path];
        }

        try {
            const content = await ideApi.getFileContent(path);
            setFileContents(prev => ({ ...prev, [path]: content }));
            return content;
        } catch (err) {
            console.error(`Failed to load file ${path}:`, err);
            return '';
        }
    };

    // Get language from file extension
    const getLanguage = (fileName) => {
        const ext = fileName.split('.').pop().toLowerCase();
        const languageMap = {
            js: 'javascript',
            jsx: 'javascript',
            ts: 'typescript',
            tsx: 'typescript',
            py: 'python',
            json: 'json',
            html: 'html',
            css: 'css',
            md: 'markdown',
        };
        return languageMap[ext] || 'plaintext';
    };

    // Handle file selection from tree
    const handleFileSelect = async (path) => {
        if (!openFiles.includes(path)) {
            setOpenFiles([...openFiles, path]);
        }
        setActiveFile(path);

        // Load file content if not already loaded
        if (fileContents[path] === undefined) {
            await getFileContent(path);
        }
    };

    // Handle tab change
    const handleTabChange = (path) => {
        setActiveFile(path);
    };

    // Handle file close
    const handleFileClose = (path) => {
        const newOpenFiles = openFiles.filter((f) => f !== path);
        setOpenFiles(newOpenFiles);

        if (activeFile === path && newOpenFiles.length > 0) {
            setActiveFile(newOpenFiles[newOpenFiles.length - 1]);
        } else if (newOpenFiles.length === 0) {
            setActiveFile(null);
        }
    };

    // Handle editor content change
    const handleEditorChange = (value) => {
        if (activeFile) {
            setFileContents({
                ...fileContents,
                [activeFile]: value,
            });
        }
    };

    // Save file to backend (or local demo storage)
    const handleSaveFile = async () => {
        const path = activeFileRef.current;
        const contents = fileContentsRef.current;

        if (!path || contents[path] === undefined) return;

        try {
            await ideApi.saveFile(path, contents[path]);
            console.log(`Saved ${path}`);
        } catch (err) {
            console.error(`Failed to save ${path}:`, err);
            alert(`Failed to save file: ${err.message}`);
        }
    };

    // Handle editor mount
    const handleEditorMount = (editor, monaco) => {
        editorRef.current = editor;

        // Add Ctrl+S / Cmd+S save shortcut
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
            void handleSaveFile();
        });
    };

    // Open in Notebook
    const handleOpenInNotebook = () => {
        if (activeFile) {
            const content = fileContents[activeFile] || '';
            navigate('/notebook', {
                state: {
                    fromIDE: true,
                    fileName: activeFile,
                    content: content,
                }
            });
        }
    };

    if (loading) {
        return (
            <div className="ide-container">
                <div className="editor-empty">
                    <div className="empty-state">
                        <h3>Loading IDE...</h3>
                        <p>Connecting to backend...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ide-container">
                <div className="editor-empty">
                    <div className="empty-state">
                        <h3>⚠️ Backend Connection Error</h3>
                        <p>{error}</p>
                        <button onClick={loadFileTree} className="btn-toolbar">
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ide-container">
            <div className="ide-toolbar">
                <div className="toolbar-left">
                    <h2 className="ide-title">IDE</h2>
                    {activeFile && (
                        <span className="current-file">
                            {activeFile}
                        </span>
                    )}
                </div>
                <div className="toolbar-right">
                    <button
                        className="btn-toolbar"
                        onClick={handleSaveFile}
                        disabled={!activeFile}
                        title="Save File (Ctrl+S)"
                    >
                        <span>💾</span>
                        <span>Save</span>
                    </button>
                    <button
                        className="btn-toolbar"
                        onClick={handleOpenInNotebook}
                        disabled={!activeFile}
                        title="Open in Notebook"
                    >
                        <img src="/svg/canvas-code-with-me.svg" alt="" style={{ width: '16px', height: '16px' }} />
                        <span>Open in Notebook</span>
                    </button>
                    <button className="btn-toolbar" title="Run">
                        <img src="/svg/play-circle.svg" alt="" style={{ width: '16px', height: '16px' }} />
                        <span>Run</span>
                    </button>
                </div>
            </div>

            <div className="ide-main">
                <div className="ide-sidebar">
                    <FileTree
                        files={files}
                        onFileSelect={handleFileSelect}
                        selectedFile={activeFile}
                    />
                </div>

                <div className="ide-editor-area">
                    <EditorTabs
                        openFiles={openFiles}
                        activeFile={activeFile}
                        onFileChange={handleTabChange}
                        onFileClose={handleFileClose}
                    />

                    <div className="editor-wrapper">
                        {activeFile ? (
                            <Editor
                                height="100%"
                                language={getLanguage(activeFile)}
                                value={fileContents[activeFile] || ''}
                                onChange={handleEditorChange}
                                onMount={handleEditorMount}
                                theme="vs"
                                options={{
                                    fontSize: 14,
                                    minimap: { enabled: true },
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    tabSize: 2,
                                    wordWrap: 'on',
                                }}
                            />
                        ) : (
                            <div className="editor-empty">
                                <div className="empty-state">
                                    <h3>No file selected</h3>
                                    <p>Select a file from the sidebar to start editing</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="ide-terminal" style={{ height: `${terminalHeight}px` }}>
                <Terminal />
            </div>
        </div>
    );
}

export default IDE;
